import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import * as Yup from "yup";
import { Stack } from "@mui/material";

import messages from "../../../../assets/locale/messages";
import { UIButton, UIDatePicker, UIModal } from "../../../../components/ui";
import { SA_UTC_OFFSET_MINUTES } from "../../../../utils/Constants";
import { endNotBeforeStart } from "../../../../utils/dateRangeValidation";
import {
  addApplicantTimeSheet,
  editApplicantTimeSheetRequest,
} from "../../../../store/ApplicantsTimeSheet/actions";
import "./ApplicantReportModal.scss";

dayjs.extend(utc);

const formatDateTimeForApi = (selectedDate) =>
  selectedDate
    ? dayjs(selectedDate).utcOffset(SA_UTC_OFFSET_MINUTES).format()
    : "";

const ApplicantReportModal = ({
  open,
  handleClose,
  selectedItem,
  isDaily,
  dateParam,
}) => {
  const dispatch = useDispatch();
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [selectedItemId, setSelectedItem] = useState();
  const list = useSelector((state) => state.applicantsTimeSheetRequest.list);

  const lang = useSelector((state) => state.locale.lang);
  const { shared, timeSheet, inputsValidations } = messages[lang];
  const { full_name_en } = list[0]?.user || "";
  const { full_name_ar } = list[0]?.user || "";

  const { eventid, userid } = useParams();

  const getInitialValues = (slotValues) => ({
    check_in_at: slotValues ? slotValues.check_in_at : "",
    check_out_at: slotValues ? slotValues.check_out_at ?? "" : "",
  });

  const {
    setValues,
    handleSubmit,
    values,
    setFieldTouched,
    setFieldValue,
    touched,
    errors,
    setTouched,
    dirty,
    isValid,
  } = useFormik({
    initialValues: getInitialValues(),
    validationSchema: Yup.object({
      check_in_at: Yup.string().required("generalFieldRequired"),
      check_out_at: Yup.string()
        .optional()
        .nullable()
        .test(
          "checkout-after-checkin",
          "checkoutCannotBeBeforeCheckin",
          endNotBeforeStart("check_in_at")
        ),
    }),
    onSubmit: async ({ check_in_at, check_out_at }) => {
      const checkoutValue = check_out_at || null;

      if (selectedItemId) {
        dispatch(
          editApplicantTimeSheetRequest({
            data: {
              check_in_at,
              check_out_at: checkoutValue,
            },
            id: selectedItemId,
            event_id: eventid,
            user_id: userid,
            isDaily,
            dateParam,
          })
        );
      } else {
        dispatch(
          addApplicantTimeSheet({
            data: {
              check_in_at,
              check_out_at: checkoutValue,
              event_id: eventid,
              user_id: userid,
            },
            isDaily,
            dateParam,
          })
        );
      }
      handleCloseStatusModal();
    },
  });

  useEffect(() => {
    setOpenStatusModal(open);
    setSelectedItem(selectedItem?.id);
  }, [open, selectedItem?.id]);

  const handleCloseStatusModal = () => {
    setOpenStatusModal(false);
    handleClose(false);
    setValues(getInitialValues());
    setTouched({});
  };

  useEffect(() => {
    setValues(getInitialValues(selectedItem));
  }, [selectedItem, lang, setValues]);

  const resolveFieldError = (field) =>
    touched[field] && errors[field]
      ? inputsValidations[errors[field]] || errors[field]
      : undefined;

  const handleDateTimeChange = (field) => (_formatted, nextValue) => {
    setFieldTouched(field);
    setFieldValue(field, formatDateTimeForApi(nextValue));
  };

  const modalTitle = `${selectedItemId ? timeSheet.EditStatus : timeSheet.addStatusFor} ${
    lang === "ar"
      ? selectedItem?.user?.full_name_ar || full_name_ar
      : selectedItem?.user?.full_name_en || full_name_en
  }`;

  const dateTimeInputFormat =
    lang === "ar" ? "YYYY/MM/DD hh:mm A" : "DD/MM/YYYY hh:mm A";

  return (
    <UIModal
      open={openStatusModal}
      onClose={handleCloseStatusModal}
      title={modalTitle}
      maxWidth="md"
      showClose={false}
      PaperProps={{ className: "applicant-report-modal" }}
      actions={
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          width="100%"
          className="applicant-report-modal__actions"
        >
          <UIButton
            type="submit"
            form="applicant-report-form"
            appearance="gradient"
            disabled={!dirty || !isValid}
          >
            {shared.save}
          </UIButton>
          <UIButton
            type="button"
            variant="outlined"
            color="inherit"
            onClick={handleCloseStatusModal}
          >
            {shared.cancel}
          </UIButton>
        </Stack>
      }
    >
      <form
        id="applicant-report-form"
        className="applicant-report-modal__form"
        noValidate
        onSubmit={handleSubmit}
      >
        <UIDatePicker
          id="check-in"
          name="check_in_at"
          withTime
          disableFuture
          label={timeSheet.checkin}
          placeholder={timeSheet.checkinPlaceHolder}
          value={values.check_in_at}
          required
          inputFormat={dateTimeInputFormat}
          error={Boolean(touched.check_in_at && errors.check_in_at)}
          helperText={resolveFieldError("check_in_at")}
          onChange={handleDateTimeChange("check_in_at")}
          classNames={{
            label: "applicant-report-modal__check-in-label",
          }}
        />
        <UIDatePicker
          id="check_out"
          name="check_out_at"
          withTime
          disableFuture
          label={timeSheet.checkout}
          placeholder={timeSheet.checkoutPlaceHolder}
          value={values.check_out_at}
          inputFormat={dateTimeInputFormat}
          error={Boolean(touched.check_out_at && errors.check_out_at)}
          helperText={resolveFieldError("check_out_at")}
          onChange={handleDateTimeChange("check_out_at")}
          classNames={{
            label: "applicant-report-modal__check-out-label",
          }}
        />
      </form>
    </UIModal>
  );
};

export default ApplicantReportModal;

ApplicantReportModal.propTypes = {
  open: PropTypes.bool,
  selectedItem: PropTypes.object,
  handleClose: PropTypes.func,
  isDaily: PropTypes.bool,
  dateParam: PropTypes.string,
};
